import React, { useState } from "react";
import { X, ArrowRight, Eye, EyeSlash, WifiHigh } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { LOGIN, REGISTER } from "@/constants/testIds";
import AegisLogo from "@/components/AegisLogo";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AuthModal({ open, onClose, initialMode = "login" }) {
    const { login, register } = useAuth();
    const [mode, setMode] = useState(initialMode);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);
    const [showPw, setShowPw] = useState(false);
    const [errMsg, setErrMsg] = useState("");
    const [diagBusy, setDiagBusy] = useState(false);
    const [diag, setDiag] = useState(null);

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        setErrMsg("");
        setDiag(null);
        try {
            if (mode === "login") {
                await login(email, password);
                toast.success("Signed in to Aegis One");
            } else {
                if (!name.trim()) throw new Error("Display name required");
                await register({ email, password, display_name: name });
                toast.success("Welcome to Aegis One");
            }
            onClose();
        } catch (err) {
            const msg = formatApiError(err, "Authentication failed");
            setErrMsg(msg);
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    const testConnection = async () => {
        setDiagBusy(true);
        setDiag(null);
        const started = Date.now();
        try {
            const { data, status } = await api.get("/health", { timeout: 8000 });
            setDiag({
                ok: true,
                status,
                ms: Date.now() - started,
                ts: data?.ts,
            });
        } catch (err) {
            setDiag({
                ok: false,
                ms: Date.now() - started,
                code: err?.code || "unknown",
                message: err?.message || "unknown",
                status: err?.response?.status || null,
            });
        } finally {
            setDiagBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(5,7,14,0.72)", backdropFilter: "blur(10px)" }}
            onClick={onClose}
        >
            <div
                className="card w-full max-w-md relative overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                data-testid="auth-modal"
            >
                <button
                    onClick={onClose}
                    className="absolute top-3 right-3 text-[var(--text-3)] hover:text-[var(--text)] z-10 p-1.5"
                    aria-label="Close"
                >
                    <X size={16} />
                </button>
                <div className="relative p-8">
                    <div className="flex items-center gap-3 mb-6">
                        <AegisLogo size={36} />
                        <div>
                            <div className="text-[20px] font-semibold tracking-tight text-[var(--text)]">Aegis One</div>
                            <div className="text-[11px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)] mt-0.5">DePIN Mining Network</div>
                        </div>
                    </div>

                    <h2 className="text-[24px] font-bold text-[var(--text)] tracking-[-0.02em] mb-1">
                        {mode === "login" ? "Sign in" : "Create your account"}
                    </h2>
                    <p className="text-[13.5px] text-[var(--text-2)] mb-6 leading-[1.55]">
                        {mode === "login"
                            ? "Access your wallet, mining positions and auto-pool withdrawals."
                            : "Join the Aegis DePIN network. Deposit USDT, buy hashrate, mine AONE."}
                    </p>

                    <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
                        {mode === "register" && (
                            <div>
                                <label className="eyebrow block mb-1.5" style={{ letterSpacing: "0.14em" }}>Display name</label>
                                <input
                                    data-testid={REGISTER.nameInput}
                                    type="text" value={name} onChange={(e) => setName(e.target.value)}
                                    className="input"
                                    placeholder="Your name"
                                    required
                                    autoComplete="name"
                                />
                            </div>
                        )}
                        <div>
                            <label className="eyebrow block mb-1.5" style={{ letterSpacing: "0.14em" }}>Email</label>
                            <input
                                data-testid={mode === "login" ? LOGIN.emailInput : REGISTER.emailInput}
                                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                                className="input font-mono text-[13px]"
                                placeholder="you@email.com"
                                required
                                autoComplete="email"
                                inputMode="email"
                            />
                        </div>
                        <div>
                            <label className="eyebrow block mb-1.5" style={{ letterSpacing: "0.14em" }}>Password</label>
                            <div className="relative">
                                <input
                                    data-testid={mode === "login" ? LOGIN.passwordInput : REGISTER.passwordInput}
                                    type={showPw ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="input font-mono text-[13px] pr-11"
                                    placeholder="Minimum 6 characters"
                                    required
                                    minLength={6}
                                    autoComplete={mode === "login" ? "current-password" : "new-password"}
                                />
                                <button
                                    type="button"
                                    data-testid="password-toggle"
                                    onClick={() => setShowPw((v) => !v)}
                                    aria-label={showPw ? "Hide password" : "Show password"}
                                    className="absolute inset-y-0 right-0 flex items-center justify-center w-11 text-[var(--text-3)] hover:text-[var(--text)] transition-colors"
                                >
                                    {showPw ? <EyeSlash size={16} weight="regular" /> : <Eye size={16} weight="regular" />}
                                </button>
                            </div>
                        </div>

                        {errMsg && (
                            <div
                                data-testid="auth-inline-error"
                                role="alert"
                                className="text-[12.5px] leading-[1.55] rounded-md px-3 py-2.5"
                                style={{
                                    background: "rgba(239,68,68,0.10)",
                                    border: "1px solid rgba(239,68,68,0.35)",
                                    color: "#fca5a5",
                                }}
                            >
                                <div>{errMsg}</div>
                                <button
                                    type="button"
                                    data-testid="test-connection-btn"
                                    onClick={testConnection}
                                    disabled={diagBusy}
                                    className="mt-2 inline-flex items-center gap-1.5 text-[11.5px] font-medium px-2 py-1 rounded"
                                    style={{
                                        background: "rgba(239,68,68,0.14)",
                                        border: "1px solid rgba(239,68,68,0.42)",
                                        color: "#fecaca",
                                    }}
                                >
                                    <WifiHigh size={12} weight="regular" />
                                    {diagBusy ? "Testing…" : "Test connection"}
                                </button>
                                {diag && (
                                    <div
                                        data-testid="auth-connection-diag"
                                        className="mt-2 text-[11px] font-mono leading-[1.55]"
                                        style={{ color: diag.ok ? "#86efac" : "#fca5a5" }}
                                    >
                                        {diag.ok ? (
                                            <>✔ Server reachable · HTTP {diag.status} · {diag.ms}ms</>
                                        ) : (
                                            <>
                                                ✕ Blocked at network layer · {diag.ms}ms
                                                <br />code: {diag.code} · status: {diag.status ?? "no response"}
                                                <br />hint: try mobile-data instead of Wi-Fi (or vice-versa), or a different browser
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        <button
                            data-testid={mode === "login" ? LOGIN.submitButton : REGISTER.submitButton}
                            type="submit"
                            disabled={busy}
                            className="btn-primary w-full mt-2 justify-center !py-3"
                        >
                            {busy ? "Working..." : (
                                <>
                                    {mode === "login" ? "Sign in" : "Create account"} <ArrowRight size={13} />
                                </>
                            )}
                        </button>
                    </form>

                    <div className="text-center mt-6 text-[12.5px] text-[var(--text-3)]">
                        {mode === "login" ? (
                            <>New here?{" "}
                                <button
                                    data-testid={LOGIN.registerLink}
                                    onClick={() => { setMode("register"); setErrMsg(""); }}
                                    className="text-[var(--primary-bright)] hover:underline font-medium"
                                >
                                    Create an account
                                </button>
                            </>
                        ) : (
                            <>Already have one?{" "}
                                <button
                                    data-testid={REGISTER.loginLink}
                                    onClick={() => { setMode("login"); setErrMsg(""); }}
                                    className="text-[var(--primary-bright)] hover:underline font-medium"
                                >
                                    Sign in
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
